import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Share2, MessageCircle, Link2, Check, Download, NotebookPen, Sparkles, Timer } from 'lucide-react';
import { TOKENS } from './glowUpGapNarratives';
import { trackLead, trackCustomEvent } from '@/utils/metaPixel';

const API = `${window.location.origin}/api`;
const T = { ...TOKENS, bg: '#FFFCF7', brand: '#726158' };
const MARK_SRC = '/images/ambition-brand-mark.png';
const WORDMARK_SRC = '/images/ambition-brand-wordmark.png';
const FAVICON_SRC = '/ambition-brand-favicon.png';
const TOUCH_ICON_SRC = '/ambition-brand-touch-icon.png';
const OG_IMAGE = 'https://beambitious.chat/images/becoming-her-og.png';
const PDF_URL = '/downloads/becoming-her-prompts.pdf';
const SHARE_URL = 'https://beambitious.chat/becomingher?utm_source=friend&utm_medium=share&utm_campaign=becoming-her';
const SHARE_TEXT = "I just grabbed the free Becoming Her Prompts — meet the woman you're becoming, then start living like her this week. Get yours:";
const PAGE_TITLE = 'Becoming Her Prompts | Free Prompt Pack';
const PAGE_DESC =
  "Meet the woman you're becoming. Get the free Becoming Her AI prompt and 7 days of future-self journal prompts.";

const DELIVERABLES = [
  {
    Icon: NotebookPen,
    title: '7 days of future-self journal prompts',
    body: 'one a day, written as if your 90-day goal is already done.',
  },
  {
    Icon: Sparkles,
    title: 'The Becoming Her AI prompt',
    body: "copy it into any AI tool and get a one-page profile of the woman you're becoming.",
  },
  {
    Icon: Timer,
    title: 'A simple 5-minute daily guide',
    body: 'how to use the prompts and turn each one into one step you take today.',
  },
];

const inputStyle = {
  width: '100%',
  padding: '16px 18px',
  fontSize: '16px',
  border: `1px solid ${T.cardBorder}`,
  borderRadius: '12px',
  background: T.cardBg,
  color: T.dark,
  fontFamily: T.sans,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 200ms ease',
};

const labelStyle = { display: 'block', fontSize: '13px', color: T.muted, marginBottom: '8px', fontFamily: T.sans };

const useMetaTags = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;
    const upsert = (selector, attr, value, tag = 'meta') => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (created) {
        el = document.createElement(tag);
        const [, key, name] = selector.match(/\[(\w+)="([^"]+)"\]/);
        el.setAttribute(key, name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return () => (created ? el.remove() : el.setAttribute(attr, prev ?? ''));
    };
    const restores = [
      upsert('meta[name="description"]', 'content', PAGE_DESC),
      upsert('meta[property="og:title"]', 'content', PAGE_TITLE),
      upsert('meta[property="og:description"]', 'content', PAGE_DESC),
      upsert('meta[property="og:image"]', 'content', OG_IMAGE),
      upsert('meta[property="og:url"]', 'content', 'https://beambitious.chat/becomingher'),
      upsert('meta[name="twitter:title"]', 'content', PAGE_TITLE),
      upsert('meta[name="twitter:description"]', 'content', PAGE_DESC),
      upsert('meta[name="twitter:image"]', 'content', OG_IMAGE),
      upsert('link[rel="icon"]', 'href', FAVICON_SRC, 'link'),
      upsert('link[rel="apple-touch-icon"]', 'href', TOUCH_ICON_SRC, 'link'),
    ];
    return () => {
      document.title = prevTitle;
      restores.forEach((fn) => fn());
    };
  }, []);
};

const Header = () => (
  <header style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', marginBottom: '36px' }} data-testid="becomingher-header">
    <img src={MARK_SRC} alt="The Ambition Brand" style={{ width: 64, height: 64, borderRadius: '50%', boxShadow: '0 6px 18px rgba(114, 97, 88, 0.18)' }} data-testid="becomingher-logo" />
    <img src={WORDMARK_SRC} alt="" aria-hidden="true" style={{ height: 11, width: 'auto', opacity: 0.9 }} data-testid="becomingher-wordmark" />
  </header>
);

const GoldDivider = () => <div style={{ width: 60, height: 1, background: T.gold, margin: '28px auto', opacity: 0.7 }} />;

const Deliverables = () => (
  <section data-testid="becomingher-deliverables">
    <h2 style={{ fontFamily: T.serif, fontWeight: 400, fontSize: '20px', color: T.dark, textAlign: 'center', margin: '0 0 20px' }}>
      What you&apos;ll receive
    </h2>
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {DELIVERABLES.map(({ Icon, title, body }, i) => (
        <li
          key={title}
          style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: '14px', padding: '18px 18px' }}
          data-testid={`becomingher-deliverable-${i + 1}`}
        >
          <span style={{ flex: '0 0 44px', width: 44, height: 44, borderRadius: '50%', background: T.goldSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={20} color={T.gold} strokeWidth={1.75} />
          </span>
          <p style={{ margin: 0, fontSize: '15px', lineHeight: 1.55, color: T.dark }}>
            <strong style={{ fontWeight: 600 }}>{title}:</strong> {body}
          </p>
        </li>
      ))}
    </ul>
  </section>
);

const SignupForm = ({ onSuccess, utm }) => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const focus = (e) => (e.target.style.borderColor = T.gold);
  const blur = (e) => (e.target.style.borderColor = T.cardBorder);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = firstName.trim();
    const mail = email.trim();
    if (!name) return setError('Please enter your first name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) return setError('Please enter a valid email address.');
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API}/becoming-her/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: name, email: mail, ...utm }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Something went wrong. Please try again.');
      trackLead({ content_name: 'Becoming Her Prompts', content_category: 'lead_magnet' });
      onSuccess();
    } catch (err) {
      setError(typeof err.message === 'string' ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 8 }} data-testid="becomingher-form" noValidate>
      <label htmlFor="bh-firstname" style={labelStyle}>First name</label>
      <input id="bh-firstname" type="text" autoComplete="given-name" autoCapitalize="words" placeholder="Your first name" value={firstName}
        onChange={(e) => setFirstName(e.target.value)} onFocus={focus} onBlur={blur} style={inputStyle} required data-testid="becomingher-firstname-input" />

      <label htmlFor="bh-email" style={{ ...labelStyle, margin: '20px 0 8px' }}>Email</label>
      <input id="bh-email" type="email" inputMode="email" autoComplete="email" placeholder="Your email address" value={email}
        onChange={(e) => setEmail(e.target.value)} onFocus={focus} onBlur={blur} style={inputStyle} required data-testid="becomingher-email-input" />

      {error && (
        <p style={{ color: '#B23A48', fontSize: '13px', marginTop: '12px', textAlign: 'center' }} role="alert" data-testid="becomingher-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading}
        data-testid="becomingher-submit-button"
        style={{
          marginTop: '28px', width: '100%', background: T.gold, color: '#FFFFFF', border: 'none', borderRadius: '30px', padding: '18px 24px',
          fontSize: '16px', fontWeight: 600, fontFamily: T.sans, cursor: loading ? 'default' : 'pointer', letterSpacing: '0.01em',
          opacity: loading ? 0.8 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          transition: 'transform 150ms ease, box-shadow 200ms ease', boxShadow: '0 1px 2px rgba(61, 50, 41, 0.08)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(184, 149, 106, 0.28)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(61, 50, 41, 0.08)'; }}
      >
        {loading && <Loader2 size={18} className="animate-spin" />}
        {loading ? 'Sending…' : 'Send Me the Prompts'}
      </button>

      <p style={{ fontSize: '11px', color: T.muted, textAlign: 'center', marginTop: '16px', letterSpacing: '0.02em' }}>
        Free. Delivered to your inbox instantly. Unsubscribe anytime.
      </p>
      <p style={{ fontSize: '12px', color: T.dark, textAlign: 'center', marginTop: '10px', lineHeight: 1.5 }} data-testid="becomingher-spam-reminder">
        Sis, peek in your <strong style={{ fontWeight: 600 }}>Spam</strong> and <strong style={{ fontWeight: 600 }}>Promotions</strong> folders — your journal prompts sometimes land there first.
      </p>
    </form>
  );
};

const shareBtnStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flex: '1 1 0', minWidth: 0,
  padding: '13px 14px', borderRadius: '30px', border: `1px solid ${T.cardBorder}`, background: T.bg, color: T.dark,
  fontFamily: T.sans, fontSize: '14px', fontWeight: 500, cursor: 'pointer', transition: 'border-color 200ms ease, background-color 200ms ease',
};

const ShareButtons = () => {
  const [copied, setCopied] = useState(false);
  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;
  const isTouch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
  const smsHref = `sms:?&body=${encodeURIComponent(`${SHARE_TEXT} ${SHARE_URL}`)}`;

  const hover = (e) => { e.currentTarget.style.borderColor = T.gold; e.currentTarget.style.backgroundColor = T.goldSoft; };
  const unhover = (e) => { e.currentTarget.style.borderColor = T.cardBorder; e.currentTarget.style.backgroundColor = T.bg; };

  const nativeShare = async () => {
    try {
      await navigator.share({ title: 'Becoming Her Prompts', text: SHARE_TEXT, url: SHARE_URL });
      trackCustomEvent('BecomingHerShare', { method: 'native' });
    } catch (_) { /* user dismissed */ }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
    } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = SHARE_URL; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
    }
    setCopied(true);
    trackCustomEvent('BecomingHerShare', { method: 'copy' });
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div style={{ marginTop: '28px', paddingTop: '24px', borderTop: `1px solid ${T.cardBorder}` }} data-testid="becomingher-share">
      <p style={{ margin: '0 0 14px', fontSize: '13px', color: T.muted, letterSpacing: '0.02em' }}>
        Know a woman who needs this too? Send it her way.
      </p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {canNativeShare && (
          <button type="button" onClick={nativeShare} style={shareBtnStyle} onMouseEnter={hover} onMouseLeave={unhover} data-testid="becomingher-share-native">
            <Share2 size={16} color={T.gold} /> Share
          </button>
        )}
        {isTouch && (
          <a href={smsHref} onClick={() => trackCustomEvent('BecomingHerShare', { method: 'sms' })} style={{ ...shareBtnStyle, textDecoration: 'none' }}
            onMouseEnter={hover} onMouseLeave={unhover} data-testid="becomingher-share-sms">
            <MessageCircle size={16} color={T.gold} /> Text a friend
          </a>
        )}
        <button type="button" onClick={copyLink} style={shareBtnStyle} onMouseEnter={hover} onMouseLeave={unhover} data-testid="becomingher-share-copy">
          {copied ? <Check size={16} color={T.gold} /> : <Link2 size={16} color={T.gold} />} {copied ? 'Link copied' : 'Copy link'}
        </button>
      </div>
    </div>
  );
};

const SuccessState = () => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}
    style={{ textAlign: 'center', background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: '16px', padding: '36px 24px' }}
    data-testid="becomingher-success">
    <h2 style={{ fontFamily: T.serif, fontWeight: 400, fontSize: 'clamp(26px, 6vw, 34px)', color: T.dark, margin: 0, lineHeight: 1.2 }}
      data-testid="becomingher-success-headline">
      Check your inbox, <span style={{ color: T.gold }}>sis.</span>
    </h2>
    <p style={{ fontSize: '15px', lineHeight: 1.6, color: T.muted, maxWidth: 400, margin: '16px auto 0' }} data-testid="becomingher-success-text">
      Your Becoming Her Prompts are on the way. If you don&apos;t see them in a few minutes, check your <strong style={{ color: T.dark, fontWeight: 600 }}>Promotions</strong> or <strong style={{ color: T.dark, fontWeight: 600 }}>Spam</strong> folder — and drag the email to your Primary inbox so the rest of the series finds you.
    </p>
    <a
      href={PDF_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackCustomEvent('BecomingHerDownload', {})}
      data-testid="becomingher-download-button"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: '24px', width: '100%', boxSizing: 'border-box',
        background: T.gold, color: '#FFFFFF', textDecoration: 'none', borderRadius: '30px', padding: '17px 24px', fontSize: '16px', fontWeight: 600,
        fontFamily: T.sans, letterSpacing: '0.01em', boxShadow: '0 1px 2px rgba(61, 50, 41, 0.08)', transition: 'transform 150ms ease, box-shadow 200ms ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(184, 149, 106, 0.28)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(61, 50, 41, 0.08)'; }}
    >
      <Download size={18} /> Download Your Prompts
    </a>
    <ShareButtons />
  </motion.div>
);

const readUtm = () => {
  const p = new URLSearchParams(window.location.search);
  return { utm_source: p.get('utm_source') || '', utm_medium: p.get('utm_medium') || '', utm_campaign: p.get('utm_campaign') || '' };
};

export default function BecomingHer() {
  const [done, setDone] = useState(false);
  const [utm] = useState(readUtm);
  useMetaTags();

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.dark, fontFamily: T.sans, padding: '32px 20px 64px', display: 'flex', flexDirection: 'column', alignItems: 'center', WebkitFontSmoothing: 'antialiased' }}
      data-testid="becomingher-page">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }} style={{ width: '100%', maxWidth: 560 }}>
        <Header />

        <div style={{ color: T.gold, fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600, textAlign: 'center', marginBottom: '20px' }}
          data-testid="becomingher-top-label">
          Free Prompt Pack
        </div>

        <h1 style={{ fontFamily: T.serif, fontWeight: 400, fontSize: 'clamp(36px, 7vw, 52px)', lineHeight: 1.1, textAlign: 'center', margin: 0, color: T.dark }}
          data-testid="becomingher-headline">
          Becoming Her <span style={{ color: T.gold }}>Prompts</span>
        </h1>

        <p style={{ fontSize: '16px', lineHeight: 1.6, color: T.muted, textAlign: 'center', maxWidth: 420, margin: '24px auto 0' }} data-testid="becomingher-subheadline">
          Meet the woman you&apos;re becoming. Then start living like her this week.
        </p>

        <GoldDivider />
        <Deliverables />
        <GoldDivider />

        <AnimatePresence mode="wait">
          {done ? <SuccessState key="success" /> : <SignupForm key="form" utm={utm} onSuccess={() => setDone(true)} />}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
